/**
 * First-login step (master brief section 7): "Which practice software do
 * you know best?" and a suggested interface mode. Shown in the page flow,
 * never as a blocking dialog, to an employee who has not chosen a mode and
 * has not answered yet, and only when the practice has not fixed a default.
 * Settings > Appearance asks the same question at any time, and the
 * appearance switch in every top bar (AppearanceSwitch.tsx) changes it in
 * one click; the banner points at that switch.
 */
import { Link } from '@tanstack/react-router';
import {
  KNOWN_PMS_CHOICES,
  SKIN_LABELS,
  skinForPms,
  suggestSkin,
  type KnownPms,
} from '@velora/contracts/skins';
import { Button } from '@velora/ui';
import { PanelsTopLeft } from 'lucide-react';
import { usePrefs } from '../prefs/prefs.js';
import './skins.css';

export const KNOWN_PMS_LABELS: Record<KnownPms, string> = {
  dentrix: 'Dentrix',
  opendental: 'Open Dental',
  eaglesoft: 'Eaglesoft',
  none: 'None of these',
};

export function SkinOnboarding() {
  const prefs = usePrefs();
  const show =
    prefs.serverAdopted &&
    prefs.skin === undefined &&
    !prefs.skinOnboarded &&
    prefs.skinContext.practiceDefault === null;
  if (!show) return null;
  const officeSkin = skinForPms(prefs.skinContext.officePms);
  const choose = (known: KnownPms) => prefs.set({ skin: suggestSkin(known), skinOnboarded: true });
  return (
    <section className="skin-onboard" aria-labelledby="skin-onboard-title">
      <div className="skin-onboard__text">
        <h2 id="skin-onboard-title" className="skin-onboard__title">
          Which practice software do you know best?
        </h2>
        <p className="skin-onboard__hint">
          Velora can look and work like it, so there is nothing new to learn. You are in{' '}
          <strong>{SKIN_LABELS[prefs.effectiveSkin]}</strong>
          {officeSkin !== 'velora' ? ' because your office runs that software' : ''}. You can change
          this any time with the{' '}
          <span className="skin-onboard__icon" role="img" aria-label="appearance switch">
            <PanelsTopLeft size={14} aria-hidden />
          </span>{' '}
          at the top of the screen (or <Link to="/settings/appearance">Settings, Appearance</Link>
          ).
        </p>
      </div>
      <div className="skin-onboard__choices" role="group" aria-label="Practice software you know">
        {KNOWN_PMS_CHOICES.map((k) => (
          <Button
            key={k}
            size="sm"
            variant={suggestSkin(k) === officeSkin ? 'primary' : 'secondary'}
            onClick={() => choose(k)}
          >
            {KNOWN_PMS_LABELS[k]}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => prefs.set({ skinOnboarded: true })}>
          Not now
        </Button>
      </div>
    </section>
  );
}
